/**
 * Identify the Archetype — bank-style, 24 vignettes.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type Archetype,
  ARCHETYPE_LABEL,
  ARCHETYPES,
  IDENTIFY_ARCHETYPE_BANK,
  type IdentifyArchetypeQuestion,
} from "@/features/quiz/lib/identifyArchetypeQuiz";

export function QuizIdentifyArchetype() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<IdentifyArchetypeQuestion, Archetype>({
    quizType: "identify_archetype",
    bank: IDENTIFY_ARCHETYPE_BANK,
    getCorrectAnswer: (q) => q.archetype,
    getQuestionPayload: (q) => ({ vignette: q.vignette }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Identify the Archetype"
      subtitle="Vignette → archetype (sheets 9–14)"
      description="Tests pattern-matching a player's behavior to one of the 6 canonical archetypes. The right archetype tag is what determines the correct exploit: misidentify a maniac as a station and you'll lose your stack trying to hero-call."
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <div className="rounded-xl border border-border bg-white/[0.02] p-4 text-sm text-text leading-relaxed italic">
        "{question.vignette}"
      </div>

      <p className="text-center text-text text-base">Archetype?</p>

      <div className="grid grid-cols-2 gap-3">
        {ARCHETYPES.map((arch) => (
          <QuizOption
            key={arch}
            value={arch}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            {ARCHETYPE_LABEL[arch]}
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{ARCHETYPE_LABEL[correctAnswer]}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
